/**
 * OpenUI Prompt Snippet
 *
 * Instructions that teach the model when and how to emit OpenUI Lang in chat.
 * Server-safe: builds the library prompt from Zod schemas + stub renderers
 * (no React UI imports), matching the client losi-library component set.
 *
 * Dependencies:
 * - @openuidev/lang-core (server-safe; not react-lang)
 * - zod/v4
 * - tool-specs
 * - component-names
 */

// lang-core only — never import @openuidev/react-lang here.
// build-prompt pulls this into API routes; react-lang's createContext breaks
// Next "collect page data" (Failed to collect page data for /api/chat/task).
import { createLibrary, defineComponent } from '@openuidev/lang-core'
import { z } from 'zod/v4'

import { LOSI_OPENUI_COMPONENT_NAMES } from '@/lib/openui/component-names'
import { LOSI_OPENUI_TOOL_SPECS } from '@/lib/openui/tool-specs'

const stub = () => null
const openUIChildren = z.array(z.any()).describe('Child OpenUI components')

const Text = defineComponent({
  name: 'Text',
  description: 'Body or muted text block.',
  props: z.object({
    content: z.string().describe('Text content'),
    tone: z.enum(['default', 'muted']).optional().describe('Visual tone')
  }),
  component: stub
})

const Heading = defineComponent({
  name: 'Heading',
  description: 'Section heading for generated UI.',
  props: z.object({
    title: z.string().describe('Heading title'),
    subtitle: z.string().optional().describe('Optional subtitle')
  }),
  component: stub
})

const Metric = defineComponent({
  name: 'Metric',
  description: 'Single KPI metric with optional hint. Prefer Query()-backed values.',
  props: z.object({
    label: z.string().describe('Metric label'),
    value: z.union([z.string(), z.number()]).describe('Metric value'),
    hint: z.string().optional().describe('Optional helper text')
  }),
  component: stub
})

const StatusBadge = defineComponent({
  name: 'StatusBadge',
  description: 'Small status pill.',
  props: z.object({
    label: z.string(),
    tone: z.enum(['default', 'success', 'warning', 'danger']).optional()
  }),
  component: stub
})

const ActionButton = defineComponent({
  name: 'ActionButton',
  description:
    'Button that sends a follow-up chat message (safe action; no arbitrary JS).',
  props: z.object({
    label: z.string().describe('Button label'),
    prompt: z.string().describe('User message to send when clicked'),
    variant: z
      .enum(['default', 'secondary', 'outline'])
      .optional()
      .describe('Button style')
  }),
  component: stub
})

const FollowUpBlock = defineComponent({
  name: 'FollowUpBlock',
  description: 'Row of follow-up suggestion chips that continue the chat.',
  props: z.object({
    prompts: z.array(z.string()).describe('Follow-up prompts')
  }),
  component: stub
})

const TaskCard = defineComponent({
  name: 'TaskCard',
  description:
    'Compact Spaces task summary. Pass taskId so click opens the task modal. Prefer TaskViews for boards/lists.',
  props: z.object({
    title: z.string().describe('Task title'),
    status: z.string().optional().describe('Task status'),
    due: z.string().optional().describe('Due date label'),
    summary: z.string().optional().describe('Short summary'),
    taskId: z.string().optional().describe('Space task id — opens modal on click'),
    spaceId: z.string().optional().describe('Space id (optional if Space selected)'),
    actionPrompt: z.string().optional()
  }),
  component: stub
})

const TaskViews = defineComponent({
  name: 'TaskViews',
  description:
    'PREFERRED for Space tasks. Live board/list/table/timeline/calendar/gantt. Drag updates status; click opens task modal. Omit spaceId to use selected Space.',
  props: z.object({
    title: z.string().optional(),
    spaceId: z.union([z.string(), z.null()]).optional(),
    defaultView: z
      .enum(['board', 'list', 'table', 'timeline', 'calendar', 'gantt'])
      .optional()
  }),
  component: stub
})

const NotesBrowser = defineComponent({
  name: 'NotesBrowser',
  description:
    'Live Space notes list. Click opens the note modal. Omit spaceId to use selected Space.',
  props: z.object({
    title: z.string().optional(),
    spaceId: z.union([z.string(), z.null()]).optional(),
    limit: z.number().optional()
  }),
  component: stub
})

const ContactsPanel = defineComponent({
  name: 'ContactsPanel',
  description:
    'Live Nexus CRM contacts. Click continues in chat with that contact. Omit workspaceId to use selected workspace.',
  props: z.object({
    title: z.string().optional(),
    workspaceId: z.union([z.string(), z.null()]).optional(),
    limit: z.number().optional()
  }),
  component: stub
})

const ContactCard = defineComponent({
  name: 'ContactCard',
  description: 'Compact CRM contact / lead card.',
  props: z.object({
    name: z.string().describe('Contact name'),
    company: z.string().optional().describe('Company name'),
    email: z.string().optional().describe('Email'),
    status: z.string().optional().describe('Pipeline status'),
    actionPrompt: z.string().optional()
  }),
  component: stub
})

const ApprovalCard = defineComponent({
  name: 'ApprovalCard',
  description: 'Ask the user to approve or reject a proposed action via chat.',
  props: z.object({
    title: z.string().describe('Approval title'),
    summary: z.string().describe('What the user is approving'),
    approvePrompt: z.string().describe('Message sent when Allow is clicked'),
    rejectPrompt: z.string().optional()
  }),
  component: stub
})

const BarChart = defineComponent({
  name: 'BarChart',
  description: 'Simple horizontal bar chart from label/value pairs.',
  props: z.object({
    title: z.string().optional().describe('Chart title'),
    items: z
      .array(z.object({ label: z.string(), value: z.number() }))
      .describe('Bar items')
  }),
  component: stub
})

const DataList = defineComponent({
  name: 'DataList',
  description:
    'List of title/subtitle rows — ideal for Query() results (tasks.rows, contacts.rows).',
  props: z.object({
    title: z.string().optional(),
    items: z
      .array(
        z.object({
          title: z.string(),
          subtitle: z.string().optional(),
          meta: z.string().optional()
        })
      )
      .describe('List rows')
  }),
  component: stub
})

const SelectItem = defineComponent({
  name: 'SelectItem',
  description: 'Option for Select.',
  props: z.object({
    value: z.string(),
    label: z.string()
  }),
  component: stub
})

const Input = defineComponent({
  name: 'Input',
  description:
    'Text input bound to a $variable (pass $var as value in OpenUI Lang).',
  props: z.object({
    name: z.string().describe('Field name'),
    // Keep this a plain Zod schema in the server prompt module — markReactive()
    // from lang-core is not chainable with .describe() and breaks Next collect.
    value: z.string().optional().describe('Bound $variable'),
    placeholder: z.string().optional()
  }),
  component: stub
})

const Select = defineComponent({
  name: 'Select',
  description: 'Select bound to a $variable (pass $var as value in OpenUI Lang).',
  props: z.object({
    name: z.string(),
    value: z.string().optional().describe('Bound $variable'),
    items: z.array(SelectItem.ref)
  }),
  component: stub
})

const FormControl = defineComponent({
  name: 'FormControl',
  description: 'Labeled form field wrapper.',
  props: z.object({
    label: z.string(),
    children: openUIChildren
  }),
  component: stub
})

const SubmitButton = defineComponent({
  name: 'SubmitButton',
  description:
    'Primary form submit that continues the conversation with the button label.',
  props: z.object({
    label: z.string(),
    prompt: z.string().optional()
  }),
  component: stub
})

const Form = defineComponent({
  name: 'Form',
  description: 'Form container for create/edit flows.',
  props: z.object({
    name: z.string().describe('Form name for state scoping'),
    children: openUIChildren
  }),
  component: stub
})

const Card = defineComponent({
  name: 'Card',
  description: 'Grouped card with title and child content.',
  props: z.object({
    title: z.string().optional().describe('Card title'),
    children: openUIChildren.optional().describe('Child components')
  }),
  component: stub
})

const Stack = defineComponent({
  name: 'Stack',
  description: 'Vertical or horizontal layout container for OpenUI children.',
  props: z.object({
    children: openUIChildren.describe('Child components'),
    direction: z.enum(['column', 'row']).optional().describe('Layout direction'),
    gap: z.enum(['s', 'm', 'l']).optional().describe('Spacing between children')
  }),
  component: stub
})

const losiOpenUIPromptLibrary = createLibrary({
  root: 'Stack',
  componentGroups: [
    {
      name: 'Layout',
      components: ['Stack', 'Card', 'Heading', 'Text', 'StatusBadge', 'FollowUpBlock']
    },
    {
      name: 'Data',
      components: ['Metric', 'BarChart', 'DataList']
    },
    {
      name: 'Forms',
      components: ['Form', 'FormControl', 'Input', 'Select', 'SelectItem', 'SubmitButton']
    },
    {
      name: 'Losi',
      components: [
        'TaskViews',
        'TaskCard',
        'NotesBrowser',
        'ContactsPanel',
        'ContactCard',
        'ApprovalCard',
        'ActionButton'
      ]
    }
  ],
  components: [
    Stack,
    Text,
    Heading,
    Metric,
    Card,
    ActionButton,
    TaskViews,
    TaskCard,
    NotesBrowser,
    ContactsPanel,
    ContactCard,
    ApprovalCard,
    BarChart,
    DataList,
    StatusBadge,
    FollowUpBlock,
    Form,
    FormControl,
    Input,
    Select,
    SelectItem,
    SubmitButton
  ]
})

const LIBRARY_PROMPT = losiOpenUIPromptLibrary.prompt({
  preamble: `You are generating interactive UI for Losi chat using OpenUI Lang.
When the user needs clickable / data-driven UI (task boards, notes, CRM,
metrics, approval prompts, charts, forms, action buttons), emit OpenUI Lang
inside a fenced code block tagged openui (aliases: openui-lang, oui).
Allowed components: ${LOSI_OPENUI_COMPONENT_NAMES.join(', ')}.
Prefer live Losi surfaces (TaskViews, NotesBrowser, ContactsPanel) over
static invented cards. Query()/Mutation() remain for metrics and create forms.`,
  additionalRules: [
    'Always define root = Stack([...]) as the entry point.',
    'DESIGN: Apple-clean. One surface only. NEVER nest Card inside Card, or wrap TaskViews / NotesBrowser / ContactsPanel / DataList / Metric rows in Card. Prefer Stack + Heading + live surfaces. Hairlines and typography — not boxes in boxes.',
    'For Space tasks ALWAYS prefer TaskViews (defaultView "board" for kanban). It is the real Spaces task surface: drag updates status, click opens the task modal, and the user can switch list/table/timeline/calendar/gantt.',
    'Do NOT fake a kanban with stacked TaskCard / DataList rows when TaskViews can be used.',
    'Use TaskCard only to highlight one task — pass taskId (and spaceId if known) so click opens the modal.',
    'Use NotesBrowser for Space notes and ContactsPanel for Nexus CRM — both load live data.',
    'Keep using existing Spaces markers like [SPACE_ITEM_CARD] when linking to a real Space item by id, and keep tool annotations for live tool results.',
    'Do not invent component names. Do not emit raw HTML or executable JS.',
    'Omit spaceId / workspaceId / space_id / workspace_id when the user already has a Space/workspace selected — live components and tools default to current context.',
    'For KPI strips use Query("get_task_metrics") + Metric in a row Stack. For create flows use Mutation + Form (no Card wrapper).',
    'ActionButton / FollowUpBlock / ApprovalCard prompts must be natural follow-up messages (no arbitrary code).',
    'You may mix short markdown prose with one OpenUI fence; do not dump large prose inside OpenUI Text props.',
    'Emit compact line-oriented OpenUI Lang, one statement per line.',
    'inlineMode: you may answer briefly in prose and still attach an openui fence when UI helps.'
  ],
  tools: LOSI_OPENUI_TOOL_SPECS,
  toolCalls: true,
  bindings: true,
  inlineMode: true,
  toolExamples: [
    `\`\`\`openui
metrics = Query("get_task_metrics", {}, {open: 0, done: 0, due_today: 0, total: 0})
m1 = Metric("Open", metrics.open, "Active")
m2 = Metric("Due today", metrics.due_today, "Attention")
m3 = Metric("Done", metrics.done, "Complete")
row = Stack([m1, m2, m3], "row", "s")
board = TaskViews("Tasks", null, "board")
root = Stack([Heading("Space pulse", "Drag to update · click to open"), row, board], "column", "m")
\`\`\``,
    `\`\`\`openui
board = TaskViews("Backlog", null, "list")
notes = NotesBrowser("Recent notes", null, 8)
root = Stack([Heading("Work + notes"), board, notes], "column", "m")
\`\`\``,
    `\`\`\`openui
$title = ""
createResult = Mutation("create_space_task", {title: $title})
titleField = FormControl("Title", [Input("title", $title, "Ship proposal")])
submit = SubmitButton("Create task", "Create this task")
form = Form("create_task", [titleField, submit])
board = TaskViews("Tasks", null, "board")
root = Stack([Heading("Quick capture"), form, board], "column", "m")
\`\`\``,
    `\`\`\`openui
contacts = ContactsPanel("Pipeline", null, 12)
root = Stack([Heading("Nexus contacts", "Live from CRM"), contacts], "column", "m")
\`\`\``
  ]
})

export const OPENUI_CHAT_PROMPT = `
== OpenUI Lang (structured interactive UI) ==
${LIBRARY_PROMPT}
`.trim()
